/**
 * @fileoverview Shared artwork catalog picker with policy-aware color controls.
 * @module ui-artwork/components/ArtworkPicker
 *
 * Responsibilities: Search, filter, choose and preview bundled artwork while
 *   respecting group inheritance and controlled hazard palettes.
 * Dependencies: artwork catalog, color policy, ArtworkRenderer, React.
 * Architecture: Presentation-only state; persistence is injected via callbacks.
 * Notes: No host database, API, or remote asset access.
 */
import React, { useMemo, useState } from 'react';
import { applyColorPolicy } from '../colorPolicy';
import { searchArtwork } from '../catalogQuery';
import type { ArtworkCategory, ArtworkStyle } from '../types';
import { ArtworkRenderer } from './ArtworkRenderer';

/**
 * @contract ArtworkPickerProps
 * Purpose: Defines controlled selection and optional inherited artwork inputs.
 * Fields: value, color, placeholder, onChange, groupDefault, onResetToGroupDefault, workflowPreview.
 * Rules: onChange receives the canonical artwork ID and policy-approved color.
 * Sync: The parent owns local-first persistence; the picker performs no writes.
 */
export interface ArtworkPickerProps {
  value?: string;
  color?: string;
  placeholder?: string;
  onChange: (next: { artworkId: string; color: string }) => void;
  groupDefault?: { artworkId?: string; color?: string; groupName?: string };
  onResetToGroupDefault?: () => void;
  workflowPreview?: boolean;
}

const STYLES: Array<{ value: ArtworkStyle | 'all'; label: string }> = [
  { value: 'all', label: 'All' }, { value: '3d', label: '3D' },
  { value: 'operational', label: 'Operational' }, { value: 'badge', label: 'Badges' },
  { value: 'glyph', label: 'Glyphs' },
];
const CATEGORIES: Array<{ value: ArtworkCategory | 'all'; label: string }> = [
  { value: 'all', label: 'All' }, { value: 'entity', label: 'Entity' },
  { value: 'inspection', label: 'Inspection' }, { value: 'hazard', label: 'Hazard' },
  { value: 'equipment', label: 'Equipment' }, { value: 'status', label: 'Status' },
  { value: 'navigation', label: 'Navigation' }, { value: 'ui', label: 'UI' },
];

/**
 * @component ArtworkPicker
 * Purpose: Let administrators assign artwork and its permitted color.
 * Props: ArtworkPickerProps.
 * Behavior: Search, style/category filters, group reset, and three-surface preview.
 * Data Source: Bundled package catalog; parent controls the selected artwork/color.
 * Constraints: Controlled hazard values expose allowed radios, never free color input.
 * Performance: Filters the bounded local catalog in memory.
 * @reusable
 * Usage: Compose into lookup group/value authoring surfaces with controlled props.
 * Rules: Stateful presentation; catalog selection stays controlled by the parent.
 */
export function ArtworkPicker({
  value,
  color,
  placeholder = 'Search by name, keyword, or ID',
  onChange,
  groupDefault,
  onResetToGroupDefault,
  workflowPreview = true,
}: ArtworkPickerProps) {
  const [query, setQuery] = useState('');
  const [style, setStyle] = useState<ArtworkStyle | 'all'>('all');
  const [category, setCategory] = useState<ArtworkCategory | 'all'>('all');
  const [previewLive, setPreviewLive] = useState(false);
  const artworkId = value || groupDefault?.artworkId || 'glyph:tag';
  const results = useMemo(() => searchArtwork({ query, style, category }), [query, style, category]);
  const selectedEntry = searchArtwork({ query: artworkId }).find((entry) => entry.id === artworkId)
    ?? searchArtwork().find((entry) => entry.id === artworkId);
  const inheritedColor = !value && !color ? groupDefault?.color : undefined;
  const requestedColor = color ?? inheritedColor;
  const resolvedColor = selectedEntry ? applyColorPolicy(selectedEntry, requestedColor).color : requestedColor;
  const isInherited = !value && !color;

  const chooseArtwork = (id: string) => {
    const entry = searchArtwork().find((candidate) => candidate.id === id);
    if (!entry) return;
    const next = applyColorPolicy(entry, requestedColor);
    onChange({ artworkId: entry.id, color: next.color });
  };
  const chooseColor = (nextColor: string) => {
    if (selectedEntry) onChange({ artworkId, color: applyColorPolicy(selectedEntry, nextColor).color });
  };

  return (
    <section aria-label="Artwork picker" className="space-y-3">
      {groupDefault && (
        <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
          <span>{isInherited
            ? `Inheriting from group ${groupDefault.groupName ?? 'default'}`
            : 'Overriding group default'}</span>
          {!isInherited && onResetToGroupDefault && (
            <button type="button" onClick={onResetToGroupDefault} className="font-medium text-blue-700">
              Reset to Group Default
            </button>
          )}
        </div>
      )}

      <label className="block text-sm font-medium" htmlFor="artwork-search">Search artwork</label>
      <input
        id="artwork-search"
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className="w-full rounded border px-3 py-2 text-sm"
        placeholder={placeholder}
      />

      <fieldset>
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">Style</legend>
        <div className="flex flex-wrap gap-1">
          {STYLES.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={style === option.value}
              onClick={() => setStyle(option.value)}
              className="rounded-full border px-2.5 py-1 text-xs aria-pressed:bg-slate-800 aria-pressed:text-white"
            >{option.label}</button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">Category</legend>
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={category === option.value}
              onClick={() => setCategory(option.value)}
              className="rounded-full border px-2.5 py-1 text-xs aria-pressed:bg-slate-800 aria-pressed:text-white"
            >{option.label}</button>
          ))}
        </div>
      </fieldset>

      <div aria-label="Artwork choices" className="grid max-h-56 grid-cols-3 gap-2 overflow-auto sm:grid-cols-4">
        {results.map((entry) => (
          <button
            type="button"
            key={entry.id}
            aria-label={entry.label}
            aria-pressed={entry.id === artworkId}
            onClick={() => chooseArtwork(entry.id)}
            className="flex min-h-20 flex-col items-center justify-center gap-1 rounded border p-2 text-xs aria-pressed:border-blue-600 aria-pressed:ring-2 aria-pressed:ring-blue-200"
          >
            <ArtworkRenderer artworkId={entry.id} color={resolvedColor} size={36} title={entry.label} />
            <span>{entry.label}</span>
          </button>
        ))}
        {results.length === 0 && <p className="col-span-full py-4 text-center text-sm text-slate-500">No artwork found.</p>}
      </div>

      {selectedEntry?.colorPolicy === 'customizable' && (
        <div className="flex items-center gap-3">
          <label htmlFor="artwork-color" className="text-sm font-medium">Artwork color</label>
          <input
            id="artwork-color"
            type="color"
            value={resolvedColor ?? selectedEntry.defaultColor}
            onChange={(event) => chooseColor(event.target.value)}
          />
        </div>
      )}
      {selectedEntry?.colorPolicy === 'controlled-hazard' && (
        <fieldset>
          <legend className="text-sm font-medium">Color locked to approved hazard treatments</legend>
          <div className="mt-1 grid gap-2">
            {(selectedEntry.allowedHazardColors ?? []).map((treatment) => (
              <label key={treatment.hex} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="hazard-treatment"
                  aria-label={treatment.label}
                  value={treatment.hex}
                  checked={(resolvedColor ?? selectedEntry.defaultColor).toLowerCase() === treatment.hex.toLowerCase()}
                  onChange={() => chooseColor(treatment.hex)}
                />
                <span className="h-4 w-4 rounded-full border" style={{ backgroundColor: treatment.hex }} />
                {treatment.label} <span className="text-slate-500">({treatment.standard})</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      {selectedEntry?.colorPolicy === 'fixed' && <p className="text-sm text-slate-600">Fixed palette</p>}

      <section aria-label="Artwork preview" className="rounded border p-3">
        <h3 className="mb-2 text-sm font-semibold">Artwork preview</h3>
        {workflowPreview && (
          <label className="mb-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={previewLive}
              onChange={(event) => setPreviewLive(event.target.checked)}
            />
            Preview active workflow status
          </label>
        )}
        <p className="mb-2 text-xs text-slate-500">The halo is driven by workflow status, not the artwork choice.</p>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          {(['Map Marker', 'Grid/List Row Badge', 'Detail Header'] as const).map((surface, index) => (
            <div key={surface} className="flex flex-col items-center gap-2 rounded bg-slate-50 p-2">
              <strong>{surface}</strong>
              <ArtworkRenderer
                artworkId={artworkId}
                color={resolvedColor}
                size={index === 0 ? 44 : 36}
                surface={index === 0 ? 'marker' : index === 1 ? 'row' : 'header'}
                showHalo={previewLive}
                title={selectedEntry?.label}
              />
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}

export default ArtworkPicker;
